import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { IMG } from '../../views/landing/landingData';
import { BrandBtn, Icons, Tip, type Theme } from './studioUi';
import { StudioAccountMenu } from './StudioAccountMenu';

export type StudioTab = 'preview' | 'code' | 'media' | 'analytics';
export type Device = 'desktop' | 'tablet' | 'mobile';

interface StudioHeaderProps {
    projectName: string;
    onRename: (name: string) => void;
    tab: StudioTab;
    onTabChange: (tab: StudioTab) => void;
    /** Live URL of the published site, or null when it has never been published. */
    shareUrl: string | null;
    onExport: () => void;
    exporting: boolean;
    /** mailto: with the guided support template (lives in the avatar menu). */
    helpHref: string;
    published: boolean;
    onPublish: () => void;
    theme: Theme;
    onTheme: (t: Theme) => void;
}

/**
 * Studio top bar (2026-11 studio design): back-to-dashboard + logo, WEBSITE chip,
 * inline-renamable project name, centered Preview / Code / Media / Analytics pill,
 * reload / share, Publish and the avatar menu (which also holds Help and Theme).
 * The device switcher lives in the preview toolbar (CodeStudio).
 */
export const StudioHeader: React.FC<StudioHeaderProps> = ({
    projectName, onRename, tab, onTabChange, shareUrl, onExport, exporting, helpHref, published, onPublish, theme, onTheme,
}) => {
    const isLight = theme === 'light';
    // Export stays hidden for now (see the commented-out button below).
    void onExport; void exporting;

    // Inline rename: Enter commits, Escape cancels, blur commits.
    const [renaming, setRenaming] = useState(false);
    const [draft, setDraft] = useState(projectName);
    const inputRef = useRef<HTMLInputElement>(null);
    useEffect(() => {
        if (renaming) inputRef.current?.select();
    }, [renaming]);
    const commit = () => {
        setRenaming(false);
        const next = draft.trim();
        if (next && next !== projectName) onRename(next);
    };

    // Share: copies the live URL (2 s "Link copied" feedback).
    const [copied, setCopied] = useState(false);
    const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const copyLink = async () => {
        if (!shareUrl) return;
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            if (copyTimer.current) clearTimeout(copyTimer.current);
            copyTimer.current = setTimeout(() => setCopied(false), 2000);
        } catch { /* clipboard unavailable */ }
    };
    useEffect(() => () => { if (copyTimer.current) clearTimeout(copyTimer.current); }, []);

    const seg = (active: boolean): React.CSSProperties => ({
        display: 'flex', alignItems: 'center', gap: 7, height: 30, padding: '0 14px', border: 0, borderRadius: 999, fontSize: 13, fontWeight: 700, cursor: 'pointer',
        background: active ? 'var(--hv-card)' : 'transparent', color: active ? 'var(--hv-mint-ink)' : 'var(--hv-body)',
    });
    const iconBtn: React.CSSProperties = { width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 0, padding: 0, borderRadius: 8, background: 'transparent', color: 'var(--hv-body)', cursor: 'pointer' };

    return (
        <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 12, height: 62, padding: '0 16px', borderBottom: '1px solid var(--hv-line)', background: 'var(--hv-card)' }}>
            <Link to="/dashboard" title="Back to dashboard" aria-label="Back to dashboard" className="hv-h-mint" style={{ display: 'flex', flex: 'none', alignItems: 'center', gap: 6, height: 36, padding: '0 10px 0 6px', borderRadius: 10, color: 'var(--hv-body)', textDecoration: 'none' }}>
                {Icons.chevronL({ size: 15, sw: 2.2 })}
                {isLight ? (
                    <img src={IMG.logoLight} alt="HyVay" style={{ height: 24, width: 24, display: 'block', objectFit: 'cover', mixBlendMode: 'multiply' }} />
                ) : (
                    <img src={IMG.logoDark} alt="HyVay" style={{ height: 24, width: 24, display: 'block', objectFit: 'cover' }} />
                )}
            </Link>
            <span className="hv-st-kind" style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 5, height: 22, marginLeft: -10, padding: '0 9px', borderRadius: 999, background: 'var(--hv-page-2)', border: '1px solid var(--hv-line)', fontSize: 9, letterSpacing: '0.06em', color: 'var(--hv-muted)' }}>
                {Icons.browser({ size: 11, sw: 2 })}
                WEBSITE
            </span>
            <span className="hv-st-name" style={{ flex: 'none', width: 1, height: 22, background: 'var(--hv-line-2)' }} />

            <div className="hv-st-name" style={{ flex: '0 1 auto', display: 'flex', minWidth: 0, marginLeft: -11 }}>
            {renaming ? (
                <input
                    ref={inputRef}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') commit();
                        if (e.key === 'Escape') { setDraft(projectName); setRenaming(false); }
                    }}
                    onBlur={commit}
                    aria-label="Project name"
                    placeholder="Project name"
                    style={{ flex: 'none', width: 230, height: 34, padding: '0 11px', borderRadius: 6, border: '1px solid var(--brand,#13422C)', background: 'var(--hv-card)', color: 'var(--hv-ink)', fontSize: 14, fontWeight: 700, outline: 'none' }}
                />
            ) : (
                <Tip label="Rename project" align="left" style={{ minWidth: 0 }}>
                    <button
                        type="button"
                        onClick={() => { setDraft(projectName); setRenaming(true); }}
                        className="hv-h-mint"
                        style={{ display: 'flex', alignItems: 'center', gap: 7, height: 34, padding: '0 11px', border: 0, borderRadius: 10, background: 'transparent', fontSize: 14, fontWeight: 700, letterSpacing: '-0.01em', color: 'var(--hv-ink)', cursor: 'pointer', whiteSpace: 'nowrap', maxWidth: 230, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{projectName}</span>
                        <span style={{ flex: 'none', display: 'flex' }}>{Icons.pencil({})}</span>
                    </button>
                </Tip>
            )}
            </div>

            <div style={{ flex: 1 }} />

            <div className="hv-st-tabs" style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 3, padding: 3, borderRadius: 999, background: 'var(--hv-page-2)', border: '1px solid var(--hv-line)' }}>
                <button type="button" onClick={() => onTabChange('preview')} aria-label="Preview" style={seg(tab === 'preview')}>{Icons.eye({})}<span className="hv-st-tab-label">Preview</span></button>
                <button type="button" onClick={() => onTabChange('code')} aria-label="Code" style={seg(tab === 'code')}>{Icons.code({})}<span className="hv-st-tab-label">Code</span></button>
                <button type="button" onClick={() => onTabChange('media')} aria-label="Media" style={seg(tab === 'media')}>{Icons.media({})}<span className="hv-st-tab-label">Media</span></button>
                <button type="button" data-track="studio_analytics_tab" onClick={() => onTabChange('analytics')} aria-label="Analytics" style={seg(tab === 'analytics')}>{Icons.chart({})}<span className="hv-st-tab-label">Analytics</span></button>
            </div>

            <div style={{ flex: 1 }} />

            <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
                <Tip label="Reload studio">
                    <button type="button" onClick={() => window.location.reload()} aria-label="Reload studio" className="hv-st-tool hv-h-ink" style={iconBtn}>{Icons.undo({})}</button>
                </Tip>
                <Tip label={copied ? 'Link copied' : shareUrl ? 'Copy live link' : 'Publish first to get a link'}>
                    <button type="button" data-track="studio_share" onClick={() => void copyLink()} aria-label="Share link" className={`hv-st-tool ${shareUrl ? 'hv-h-ink' : ''}`} disabled={!shareUrl} style={shareUrl ? iconBtn : { ...iconBtn, opacity: 0.45, cursor: 'not-allowed' }}>{Icons.share({})}</button>
                </Tip>
                {/* Export stays hidden (product decision); the handler is still wired in Studio.tsx.
                <Tip label="Download the source (.zip)">
                    <PillBtn className="hv-st-export" data-track="studio_export" onClick={onExport} disabled={exporting} style={exporting ? { opacity: 0.6, cursor: 'wait' } : undefined}>
                        {Icons.download({})}
                        {exporting ? 'Exporting…' : 'Export'}
                    </PillBtn>
                </Tip> */}
                <Tip label={published ? 'Republish your site' : 'Publish your site'}>
                    <BrandBtn data-track="studio_publish" onClick={onPublish} style={{ height: 34, padding: '0 16px', fontSize: 13, letterSpacing: '-0.005em' }} className="hv-h-brand-82">
                        {published ? 'Published' : 'Publish'}
                    </BrandBtn>
                </Tip>
                <StudioAccountMenu theme={theme} onTheme={onTheme} helpHref={helpHref} />
            </div>
        </div>
    );
};
